/* eslint-disable unicorn/no-null */
import { computed } from "vue";

type ConsentState = "accepted" | "declined" | "undecided";

type StoredConsent = {
    state: ConsentState;
    timestamp: number;
};

export const STORAGE_KEY = "tracking-consent";
export const STORAGE_USER_ID_KEY = "tracking-user-id";
const MAX_AGE_DAYS = 180;

export function isStoredConsent(value: unknown): value is StoredConsent {
    if (typeof value !== "object" || value === null) return false;

    const maybeConsent = value as { state?: unknown; timestamp?: unknown };

    return (
        typeof maybeConsent.state === "string" &&
        ["accepted", "declined", "undecided"].includes(maybeConsent.state) &&
        typeof maybeConsent.timestamp === "number" &&
        !Number.isNaN(maybeConsent.timestamp)
    );
}

// eslint-disable-next-line no-restricted-exports, unicorn/no-anonymous-default-export
export default () => {
    const stored = useSlugLocalStorage<StoredConsent | null>(
        STORAGE_KEY,
        null,
        {
            serializer: {
                read: (v: string): StoredConsent | null => {
                    try {
                        const parsed = JSON.parse(v) as unknown;

                        return isStoredConsent(parsed) ? parsed : null;
                    } catch {
                        return null;
                    }
                },
                write: (v: StoredConsent | null): string => {
                    return JSON.stringify(v);
                },
            },
        },
    );

    const now = useNow({ interval: 600_000 });

    const isExpired = computed(() => {
        if (!stored.value) return true;

        const age = now.value.getTime() - stored.value.timestamp;
        const maxAge = MAX_AGE_DAYS * 24 * 60 * 60 * 1000;

        return age > maxAge;
    });

    const consent = computed<ConsentState>(() => {
        if (!stored.value || isExpired.value) return "undecided";

        return stored.value.state;
    });

    function setConsent(state: ConsentState) {
        stored.value = {
            state,
            timestamp: Date.now(),
        };
    }

    function revokeConsent() {
        const reloadRequired = stored.value?.state === "accepted";

        setConsent("undecided");

        // Check if the STORAGE_USER_ID_KEY is used in the contentsquare hotjar.
        localStorage.removeItem(STORAGE_USER_ID_KEY);

        if (reloadRequired) {
            location.reload();
        }
    }

    function accept() {
        setConsent("accepted");
    }

    function decline() {
        setConsent("declined");
    }

    return {
        accept,
        consent,
        decline,
        isAccepted: computed(() => consent.value === "accepted"),
        isDeclined: computed(() => consent.value === "declined"),
        isExpired,
        isUndecided: computed(() => consent.value === "undecided"),
        revokeConsent,
        setConsent,
    };
};
